export const PAYMENT_RAILS = Object.freeze({
  COUNTER_MANUAL: "counter_manual",
  MENU_DEEPLINK: "menu_deeplink",
  KIOSK_QR: "kiosk_qr",
  WAITER_QR: "waiter_qr",
  WAITER_POINT: "waiter_point",
});

// NFC requires Mercado Pago's collaborator/Point Tap flow before it can be
// reconciled safely from Mozzo. Keep the rail disabled until that integration exists.
export const PAYMENT_RAIL_AVAILABILITY = Object.freeze({
  [PAYMENT_RAILS.WAITER_POINT]: false,
});

export const DEFAULT_PROVIDER_SPLIT_MODE = "mercado_pago_marketplace_fee";

export const COMMISSION_COLLECTION_MODES = Object.freeze({
  DEFERRED_TO_NEXT_DIGITAL: "deferred_to_next_digital",
  AUTOMATIC_ON_DIGITAL_CHARGE: "automatic_on_digital_charge",
});

const toSafeText = (value, maxLength = 80) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const PAYMENT_RAIL_LABELS = {
  [PAYMENT_RAILS.COUNTER_MANUAL]: "Cobro en caja",
  [PAYMENT_RAILS.MENU_DEEPLINK]: "Cobro por deep link",
  [PAYMENT_RAILS.KIOSK_QR]: "Cobro QR del kiosk",
  [PAYMENT_RAILS.WAITER_QR]: "Cobro QR presencial del mozo",
  [PAYMENT_RAILS.WAITER_POINT]: "Cobro con Point",
};

export const normalizePaymentRail = (value) => {
  const normalized = toSafeText(value, 40).toLowerCase();

  return Object.values(PAYMENT_RAILS).includes(normalized)
    ? normalized
    : PAYMENT_RAILS.COUNTER_MANUAL;
};

export const isPaymentRailEnabled = (value) =>
  PAYMENT_RAIL_AVAILABILITY[normalizePaymentRail(value)] !== false;

export const isDigitalPaymentRail = (value) => {
  const rail = normalizePaymentRail(value);
  return (
    rail === PAYMENT_RAILS.MENU_DEEPLINK ||
    rail === PAYMENT_RAILS.KIOSK_QR ||
    rail === PAYMENT_RAILS.WAITER_QR
  );
};

export const isCounterPaymentRail = (value) =>
  [PAYMENT_RAILS.COUNTER_MANUAL, PAYMENT_RAILS.WAITER_POINT].includes(
    normalizePaymentRail(value)
  );

export const getPaymentRailLabel = (value) =>
  PAYMENT_RAIL_LABELS[normalizePaymentRail(value)] ||
  PAYMENT_RAIL_LABELS[PAYMENT_RAILS.COUNTER_MANUAL];

export const buildPaymentRailSnapshot = ({
  source = "",
  checkoutMethod = "",
  paymentProvider = "",
  splitMode = "",
  isKioskExperience = false,
} = {}) => {
  const normalizedSource = toSafeText(source, 40).toLowerCase();
  const normalizedMethod = toSafeText(checkoutMethod, 40).toLowerCase();
  const normalizedProvider = toSafeText(paymentProvider, 40).toLowerCase();
  const normalizedSplitMode = toSafeText(splitMode, 60).toLowerCase();

  let rail = PAYMENT_RAILS.COUNTER_MANUAL;

  if (normalizedSource === "waiter" && normalizedMethod === "waiter_qr") {
    rail = PAYMENT_RAILS.WAITER_QR;
  } else if (
    normalizedSource === "waiter" &&
    ["point", "waiter_point", "card_point", "card_nfc", "nfc"].includes(
      normalizedMethod
    )
  ) {
    rail = PAYMENT_RAILS.WAITER_POINT;
  } else if (normalizedMethod === "deep_link") {
    rail = PAYMENT_RAILS.MENU_DEEPLINK;
  } else if (
    normalizedMethod === "pay_by_phone" ||
    (isKioskExperience && normalizedSource === "kiosk" && normalizedMethod === "qr")
  ) {
    rail = PAYMENT_RAILS.KIOSK_QR;
  }

  const effectiveSplitMode =
    rail === PAYMENT_RAILS.KIOSK_QR || rail === PAYMENT_RAILS.WAITER_QR
      ? DEFAULT_PROVIDER_SPLIT_MODE
      : normalizedSplitMode || DEFAULT_PROVIDER_SPLIT_MODE;

  const supportsProviderSideRetention =
    (rail === PAYMENT_RAILS.MENU_DEEPLINK ||
      rail === PAYMENT_RAILS.KIOSK_QR ||
      rail === PAYMENT_RAILS.WAITER_QR) &&
    effectiveSplitMode !== "collect_on_next_digital";
  const commissionCollectionMode = supportsProviderSideRetention
    ? COMMISSION_COLLECTION_MODES.AUTOMATIC_ON_DIGITAL_CHARGE
    : COMMISSION_COLLECTION_MODES.DEFERRED_TO_NEXT_DIGITAL;

  return {
    rail,
    label: getPaymentRailLabel(rail),
    provider: normalizedProvider || "pending_provider",
    chargePresentation:
      rail === PAYMENT_RAILS.KIOSK_QR || rail === PAYMENT_RAILS.WAITER_QR
        ? "qr_code"
        : rail === PAYMENT_RAILS.MENU_DEEPLINK
          ? "deep_link"
          : rail === PAYMENT_RAILS.WAITER_POINT
            ? "point_terminal"
          : "counter",
    commissionCollectionMode,
    splitExecutionMode:
      commissionCollectionMode ===
      COMMISSION_COLLECTION_MODES.AUTOMATIC_ON_DIGITAL_CHARGE
        ? "provider_split_or_retention"
        : "commission_debt_ledger",
    interoperabilityObjective:
      rail === PAYMENT_RAILS.KIOSK_QR
        ? "interoperable_wallet_qr"
        : rail === PAYMENT_RAILS.WAITER_QR
          ? "interoperable_wallet_qr"
          : rail === PAYMENT_RAILS.WAITER_POINT
            ? "point_terminal_card_nfc"
        : rail === PAYMENT_RAILS.MENU_DEEPLINK
          ? "digital_checkout_link"
          : "counter_collection",
    splitMode: effectiveSplitMode,
  };
};
